import ExcelJS from 'exceljs';
import { notify } from '@/utils/notify';

const FONT_NAME = 'Microsoft JhengHei';
const THIN_BORDER = {
    top: { style: 'thin', color: { argb: 'FFD9D9D9' } },
    bottom: { style: 'thin', color: { argb: 'FFD9D9D9' } },
    left: { style: 'thin', color: { argb: 'FFD9D9D9' } },
    right: { style: 'thin', color: { argb: 'FFD9D9D9' } }
};

const SUBTITLE_STYLE = {
    font: { bold: true, name: FONT_NAME },
    alignment: { vertical: 'middle' },
    border: THIN_BORDER
};

const HEADER_STYLE = {
    font: { bold: true, name: FONT_NAME },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF6EF' } },
    alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
    border: THIN_BORDER
};

const BODY_STYLE = {
    font: { name: FONT_NAME },
    alignment: { vertical: 'middle', wrapText: true },
    border: THIN_BORDER
};

const BODY_RIGHT_STYLE = {
    font: { name: FONT_NAME },
    alignment: { horizontal: 'right', vertical: 'middle', wrapText: true },
    border: THIN_BORDER
};

const SECTION_TITLE_STYLE = {
    font: { bold: true, size: 14, name: FONT_NAME },
    alignment: { horizontal: 'left', vertical: 'middle' },
    border: THIN_BORDER
};

const BASIC_INFO_LABEL_STYLE = {
    font: { bold: true, name: FONT_NAME },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF6EF' } },
    alignment: { horizontal: 'left', vertical: 'middle', wrapText: true },
    border: THIN_BORDER
};

const BASIC_INFO_VALUE_STYLE = {
    font: { name: FONT_NAME },
    alignment: { horizontal: 'left', vertical: 'middle', wrapText: true },
    border: THIN_BORDER
};

function setCellValue(worksheet, rowIndex, colIndex, value) {
    worksheet.getRow(rowIndex).getCell(colIndex).value = value;
}

function applyStyle(cell, style) {
    if (style.font) {
        cell.font = { ...style.font };
    }
    if (style.alignment) {
        cell.alignment = { ...style.alignment };
    }
    if (style.border) {
        cell.border = {
            top: style.border.top ? { ...style.border.top, color: { ...style.border.top.color } } : undefined,
            bottom: style.border.bottom ? { ...style.border.bottom, color: { ...style.border.bottom.color } } : undefined,
            left: style.border.left ? { ...style.border.left, color: { ...style.border.left.color } } : undefined,
            right: style.border.right ? { ...style.border.right, color: { ...style.border.right.color } } : undefined
        };
    }
    if (style.fill) {
        cell.fill = {
            ...style.fill,
            fgColor: style.fill.fgColor ? { ...style.fill.fgColor } : undefined
        };
    }
}

function styleRow(worksheet, rowIndex, fromCol, toCol, style) {
    for (let colIndex = fromCol; colIndex <= toCol; colIndex += 1) {
        applyStyle(worksheet.getRow(rowIndex).getCell(colIndex), style);
    }
}

function applyTableSectionStyles(worksheet, tableSections) {
    tableSections.forEach(section => {
        if (typeof section.titleRow === 'number') {
            styleRow(worksheet, section.titleRow, section.fromCol, section.toCol, SUBTITLE_STYLE);
        }

        // 表頭預設全置中 (HEADER_STYLE 內建 center)
        styleRow(worksheet, section.headerRow, section.fromCol, section.toCol, HEADER_STYLE);

        // 取得當前表格設定要靠右的欄位陣列 (例如: [2, 3, 4, 5, 6, 7, 8])
        const rightCols = section.rightAlignCols || [];

        for (let rowIndex = section.dataStartRow; rowIndex <= section.dataEndRow; rowIndex += 1) {
            for (let colIndex = section.fromCol; colIndex <= section.toCol; colIndex += 1) {
                const cell = worksheet.getRow(rowIndex).getCell(colIndex);
                
                // 💡 判斷目前的欄位號碼是否有在靠右清單中
                if (rightCols.includes(colIndex)) {
                    applyStyle(cell, BODY_RIGHT_STYLE);
                } else {
                    applyStyle(cell, BODY_STYLE);
                }
            }
        }
    });
}

function appendTitleRow(worksheet, rowIndex, titleText, mergeColumns = 9) {
    setCellValue(worksheet, rowIndex, 1, titleText || '');
    worksheet.mergeCells(rowIndex, 1, rowIndex, Math.max(1, mergeColumns));
    applyStyle(worksheet.getRow(rowIndex).getCell(1), SECTION_TITLE_STYLE);
    worksheet.getRow(rowIndex).height = 24;
    return rowIndex + 1;
}

function appendObjectRows(worksheet, rowIndex, rows, options = {}) {
    const { skipHeader = false, headers = null } = options;
    const safeRows = rows && rows.length > 0 ? rows : [{ 訊息: '暫無資料' }];
    const resolvedHeaders = headers || Object.keys(safeRows[0] || {});
    let currentRow = rowIndex;

    if (!skipHeader) {
        resolvedHeaders.forEach((header, index) => {
            setCellValue(worksheet, currentRow, index + 1, header);
        });
        currentRow += 1;
    }

    safeRows.forEach(row => {
        resolvedHeaders.forEach((header, index) => {
            setCellValue(worksheet, currentRow, index + 1, row[header] != null ? row[header] : '');
        });
        currentRow += 1;
    });

    return {
        nextRow: currentRow,
        columnCount: Math.max(1, resolvedHeaders.length)
    };
}

function appendKeyValueRows(worksheet, rowIndex, rows) {
    const safeRows = rows && rows.length > 0 ? rows : [{ 項目: '訊息', 內容: '暫無資料' }];
    const result = appendObjectRows(worksheet, rowIndex, safeRows);
    return result.nextRow;
}

function appendBasicInfoGridRows(worksheet, rowIndex, rows) {
    const safeRows = rows && rows.length > 0 ? rows : [{ 項目: '訊息', 內容: '暫無資料' }];
    const pairCountPerRow = 3;
    let currentRow = rowIndex;
    for (let startIndex = 0; startIndex < safeRows.length; startIndex += pairCountPerRow) {
        for (let pairIndex = 0; pairIndex < pairCountPerRow; pairIndex += 1) {
            const rowData = safeRows[startIndex + pairIndex] || { 項目: '', 內容: '' };
            const labelCol = pairIndex * 2 + 1;
            const valueCol = labelCol + 1;
            const labelCell = worksheet.getRow(currentRow).getCell(labelCol);
            const valueCell = worksheet.getRow(currentRow).getCell(valueCol);
            labelCell.value = rowData.項目 || '';
            valueCell.value = rowData.內容 || '';
            applyStyle(labelCell, BASIC_INFO_LABEL_STYLE);
            applyStyle(valueCell, BASIC_INFO_VALUE_STYLE);
        }
        currentRow += 1;
    }
    return currentRow;
}

function appendTableSection(worksheet, rowIndex, subtitle, rows, mergeColumns = 9, tableSections = null, rightAlignCols = []) {
    let currentRow = rowIndex;
    const titleRow = subtitle ? currentRow : null;

    if (subtitle) {
        currentRow = appendTitleRow(worksheet, currentRow, subtitle, mergeColumns);
    }

    const safeRows = rows && rows.length > 0 ? rows : [{ 訊息: '暫無資料' }];
    const result = appendObjectRows(worksheet, currentRow, safeRows);

    if (Array.isArray(tableSections)) {
        tableSections.push({
            titleRow,
            headerRow: currentRow,
            dataStartRow: currentRow + 1,
            dataEndRow: result.nextRow - 1,
            fromCol: 1,
            toCol: result.columnCount,
            rightAlignCols // 👈 紀錄這一組表格哪些欄位要靠右
        });
    }

    return result.nextRow + 1;
}

function appendGroupedTables(worksheet, rowIndex, groups, mergeColumns = 9, tableSections = null) {
    let currentRow = rowIndex;
    const safeGroups = groups && groups.length > 0 ? groups : [{ title: '關係戶', rows: [{ 訊息: '暫無資料' }] }];

    safeGroups.forEach(group => {
        currentRow = appendTableSection(worksheet, currentRow, group.title || '關係戶', group.rows, mergeColumns, tableSections,[3,4]);
    });

    return currentRow;
}

function appendTotalTableWithoutHeader(worksheet, rowIndex, rows) {
    const headers = ['分行別', '授信項目', '訂約金額', '授信餘額', '貸款期間', '擔保品', '備註'];
    const safeRows = rows && rows.length > 0 ? rows : [{
        分行別: '',
        授信項目: '總計',
        訂約金額: '',
        授信餘額: '',
        貸款期間: '',
        擔保品: '',
        備註: ''
    }];

    const result = appendObjectRows(worksheet, rowIndex, safeRows, {
        skipHeader: true,
        headers
    });

    // 逐格套用樣式
    for (let rowNumber = rowIndex; rowNumber < result.nextRow; rowNumber += 1) {
        for (let colIndex = 1; colIndex <= headers.length; colIndex += 1) {
            const cell = worksheet.getRow(rowNumber).getCell(colIndex);
            
            // 💡 判斷：第 3 欄 (訂約金額) 與 第 4 欄 (授信餘額) 套用靠右樣式
            if (colIndex === 3 || colIndex === 4) {
                applyStyle(cell, BODY_RIGHT_STYLE);
            } else {
                applyStyle(cell, BODY_STYLE);
            }
        }
    }

    return result.nextRow;
}

function setWorksheetColumns(worksheet) {
    worksheet.columns = [
        { width: 24 },
        { width: 28 },
        { width: 18 },
        { width: 18 },
        { width: 18 },
        { width: 18 },
        { width: 18 },
        { width: 18 },
        { width: 18 }
    ];
}

function createOverviewWorksheet(workbook, {
    basicInfo,
    businessStatus,
    contribution,
    creditStatus
}) {
    const worksheet = workbook.addWorksheet('公司概況表');
    const tableSections = [];
    let currentRow = 1;

    currentRow = appendTitleRow(worksheet, currentRow, '一、客戶基本資料', 6);

    // 計算是否有值
    let biz_info_visible = false;
    Object.keys(basicInfo).forEach(key => {
        const value = basicInfo[key];
        if (value !== null && value !== undefined && String(value).trim() !== '') {
            biz_info_visible = true;
        }
    })
    console.log("biz_info_visible",biz_info_visible)    

    if( biz_info_visible) {
        currentRow = appendBasicInfoGridRows(worksheet, currentRow, [
            { 項目: '公司名稱', 內容: basicInfo.CUS_NAME || '' },
            { 項目: '公司英文名稱', 內容: basicInfo.CUS_ENG_NAME || '' },
            { 項目: '公司統一編號', 內容: basicInfo.CUS_IDN || '' },
            { 項目: '公司設立日', 內容: basicInfo.CUS_BIRTHDAY || '' },
            { 項目: '國別', 內容: basicInfo.CUS_COUNTRY_CODE || '' },
            { 項目: '負責人', 內容: basicInfo.CUS_REP_NAME || '' },
            { 項目: '行業別', 內容: basicInfo.CUS_IDU_NAME || '' },
            { 項目: '組織型態', 內容: basicInfo.CUS_ORG_TYPE_NAME || '' },
            { 項目: '客戶等級', 內容: basicInfo.CUS_GRADE || '' },
            { 項目: '利害關係人', 內容: basicInfo.INTEREST_REL || '' },
            { 項目: '債務協商', 內容: basicInfo.NOTE_L5 || '' }
        ]);
    } else {
        const result = appendObjectRows(worksheet, currentRow, [], {
            skipHeader: true
        });             
        currentRow +=1;
    }



    currentRow += 1;
    currentRow = appendTitleRow(worksheet, currentRow, '二、營業狀況', 8);
    currentRow = appendTableSection(worksheet, currentRow, '個體營業狀況(單位:新臺幣千元)', (businessStatus.individual || []).map(item => ({
        年度: item.DATE_IFRS || '',
        營業收入: item.SD1_IFRS || '',
        成長率: item.SD2_IFRS || '',
        營業毛利: item.SD3_IFRS || '',
        營業利益: item.SD4_IFRS || '',
        營業外收支淨額: item.SD9_IFRS || '',
        稅前損益: item.SD7_IFRS || '',
        稅前損益率: item.SD8_IFRS || ''
    })), 8, tableSections, [2, 3, 4, 5, 6, 7, 8]);
    currentRow = appendTitleRow(worksheet, currentRow, businessStatus.desc_IFRS || '', 8);
    currentRow = appendTableSection(worksheet, currentRow, '合併營業狀況(IFRS)(單位:新臺幣千元)', (businessStatus.IFRS || []).map(item => ({
        年度: item.DATE_IFRS || '',
        營業收入: item.SD1_IFRS || '',
        成長率: item.SD2_IFRS || '',
        營業毛利: item.SD3_IFRS || '',
        營業利益: item.SD4_IFRS || '',
        營業外收支淨額: item.SD9_IFRS || '',
        稅前損益: item.SD7_IFRS || '',
        稅前損益率: item.SD8_IFRS || ''
    })), 8, tableSections , [2, 3, 4, 5, 6, 7, 8]);

    currentRow += 1;
    currentRow = appendTitleRow(worksheet, currentRow, '三、主要業務貢獻度');
    currentRow = appendTableSection(worksheet, currentRow, `統計起訖日：${contribution.period || ''}(單位:新臺幣元)`, (contribution.detail || []).map(item => ({
        '業務群組-產品別': item.BUS_GROUP_LABEL_DESC || '',
        '平均餘額／交易': item.PROF_AVE_BALANCE || '',
        資金運用收入: item.PROF_FUND_INCOME || '',
        資金運用成本: item.PROF_FUND_COST || '',
        資金運用收益: item.PROF_FUND_PROF || '',
        手續費收益: item.PROF_CMS_PROF || '',
        其他收入: item.PROF_OTHER_INCOME || '',
        兌換收益: item.PROF_GA_LOS_XC || '',
        貢獻度: item.PROF_CLIENT_PROF || ''
    })), 9, tableSections, [2, 3, 4, 5, 6, 7, 8,9]);
    currentRow = appendTitleRow(worksheet, currentRow, '註：本表相關資料僅列示至單位元，倘有完整數據需求，以CRM3系統為準');
    currentRow = appendTitleRow(worksheet, currentRow, '');
    currentRow = appendTitleRow(worksheet, currentRow, '主要業務貢獻度統計');

    // 計算是否有值
    let contribution_statistics_visible = false;
    Object.keys(contribution.statistics).forEach(key => {
        const value = contribution.statistics[key];
        if (value !== null && value !== undefined && String(value).trim() !== '') {
            contribution_statistics_visible = true;
        }
    })
    console.log("contribution_statistics_visible",contribution_statistics_visible)    

    if( contribution_statistics_visible) {
        currentRow = appendBasicInfoGridRows(worksheet, currentRow, [
            { 項目: '貢獻度合計', 內容: contribution.statistics.ROA_CLIENT_PROF || '' },
            { 項目: '放款收益', 內容: contribution.statistics.ROA_FUND_PROF_LOAN || '' },
            { 項目: '保證手續費收益', 內容: contribution.statistics.ROA_1886 || '' },
            { 項目: '一般手續費收益', 內容: contribution.statistics.ROA_NON1886 || '' },
            { 項目: '存款收益', 內容: contribution.statistics.ROA_FUND_PROF_SAVING || '' },
            { 項目: '信用卡業務收益', 內容: contribution.statistics.ROA_FUND_PROF_IC || '' },
            { 項目: '融資交易資金運用收益', 內容: contribution.statistics.ROA_FUND_PROF_SMS || '' },
            { 項目: 'ROA1', 內容: contribution.statistics.ROA1 || '' },
            { 項目: 'ROA2', 內容: contribution.statistics.ROA2 || '' },
            { 項目: 'ROA3', 內容: contribution.statistics.ROA3 || '' }
        ]);
    } else {
        const result = appendObjectRows(worksheet, currentRow, [], {
            skipHeader: true
        });        
        currentRow +=1;
    }


    currentRow += 1;
    currentRow = appendTitleRow(worksheet, currentRow, '四、授信往來情形(單位:新臺幣千元)', 7);
    currentRow = appendTableSection(
        worksheet,
        currentRow,
        `${creditStatus.mainCustomer || basicInfo.CUS_NAME || ''}(${creditStatus.mainCustomerID || basicInfo.CUS_IDN || ''})`,
        (creditStatus.main || []).map(item => ({
            分行別: item.CUS_BRANCH || '',
            授信項目: item.CREDIT_ITEM || '',
            訂約金額: item.CONTRACT_AMOUNT || '',
            授信餘額: item.CREDIT_BAL || '',
            貸款期間: item.LOAN_TERM || '',
            擔保品: item.COLLATERAL || '',
            備註: item.REMARKS || ''
        })),
        7,
        tableSections, [3,4]
    );

    const childCreditGroups = [];
    (creditStatus.child || []).forEach(group => {
        if (!Array.isArray(group) || group.length === 0) {
            return;
        }

        const headerItem = group[0] || {};
        childCreditGroups.push({
            title: `${headerItem.CUS_NAME || '關係戶'}(${headerItem.CHILD_CUSIDN || ''})`,
            rows: group.map(item => ({
                分行別: item.CUS_BRANCH || '',
                授信項目: item.CREDIT_ITEM || '',
                訂約金額: item.CONTRACT_AMOUNT || '',
                授信餘額: item.CREDIT_BAL || '',
                貸款期間: item.LOAN_TERM || '',
                擔保品: item.COLLATERAL || '',
                備註: item.REMARKS || ''
            }))
        });
    });
    currentRow = appendGroupedTables(worksheet, currentRow, childCreditGroups, 7, tableSections);

    const totalRows = (creditStatus.all_subTotal || []).map(item => ({
        分行別: item.CUS_BRANCH || '',
        授信項目: item.SUM_CREDIT_ITEM || item.CREDIT_ITEM || '總計',
        訂約金額: item.SUM_CONTRACT_AMOUNT || item.CONTRACT_AMOUNT || '',
        授信餘額: item.SUM_CREDIT_BAL || item.CREDIT_BAL || '',
        貸款期間: item.SUM_LOAN_TERM || item.LOAN_TERM || '',
        擔保品: item.SUM_COLLATERAL_RATIO || item.COLLATERAL || '',
        備註: item.REMARKS || ''
    }));

    currentRow += 1;
    if( creditStatus.all_subTotal.length > 0 )
        appendTotalTableWithoutHeader(worksheet, currentRow, totalRows);

    applyTableSectionStyles(worksheet, tableSections);
    setWorksheetColumns(worksheet);
    return worksheet;
}

function downloadWorkbook(buffer, fileName) {
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

export async function exportCompanyOverviewExcel({
    comBriefVisible,
    basicInfo,
    businessStatus,
    contribution,
    creditStatus,
    fallbackCompanyName = '公司'
}) {
    try {
        if (!comBriefVisible ) {
            notify({ title: '提示', message: '目前無公司概況資料可匯出', type: 'info' });
            return false;
        }

        const workbook = new ExcelJS.Workbook();
        const companyName = basicInfo.CUS_NAME || fallbackCompanyName;
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_');

        workbook.creator = 'TBB_MIG_Web';
        workbook.created = new Date();

        createOverviewWorksheet(workbook, {
            basicInfo,
            businessStatus,
            contribution,
            creditStatus
        });

        const fileName = `${companyName.trim()}_公司概況表_${timestamp}.xlsx`;
        const buffer = await workbook.xlsx.writeBuffer();

        downloadWorkbook(buffer, fileName);

        notify({ title: '成功', message: 'Excel 已匯出', type: 'success' });
        return true;
    } catch (error) {
        console.error('匯出公司概況 Excel 失敗:', error);
        notify({ title: '錯誤', message: `Excel 匯出失敗：${error.message || '未知錯誤'}`, type: 'error' });
        return false;
    }
}
